import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import client from "../../../api/client";
import { getUserCountry, getFormattedPrice } from "../../../utils/currency";
import { getCache, setCache } from "../../../utils/cache";

const defaultIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const ArdrendCourses = () => {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(1);
  const [coursesData, setCoursesData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchCoursesAndCountry = async () => {
      try {
        const country = await getUserCountry();

        // Use centralized cache utility (handles TTL wrapper correctly)
        let coursesList = getCache('courses_data');

        if (!coursesList) {
          const res = await client.get('/v2/courses');
          coursesList = Array.isArray(res.data) ? res.data : (res.data.data || []);
          setCache('courses_data', coursesList);
        }

        // Ensure it's an array before mapping
        if (!Array.isArray(coursesList)) coursesList = [];

        const mappedCourses = coursesList.map(course => ({
          id: course.id,
          title: course.title,
          desc: (course.description || '').replace(/<[^>]*>?/gm, '').substring(0, 100) + '...',
          cost: getFormattedPrice(course, country),
          duration: course.duration || "12 Weeks",
          img: course.image_url || "/default-course.jpg",
          link: `/online-course/${course.slug}`,
          icon: defaultIcon
        }));
        setCoursesData([...mappedCourses].reverse().slice(0, 6));
      } catch (err) {
        console.error("Error fetching courses", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCoursesAndCountry();
  }, []);

  return (
    <section id="services" className="relative w-full bg-white pt-20 pb-20">
      <div className="w-full max-w-[1560px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-8">
          <div className="w-full lg:w-1/2">
            <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-6 flex items-center gap-3">
              <span className="block w-8 h-[2px] bg-primary"></span>
              {t("home.courses.kicker")}
            </div>
            <h2 className="text-[#0b0f19] text-3xl md:text-5xl font-bold relative inline-block">
              {t("home.courses.title")}<span className="text-primary">.</span>
            </h2>
          </div>
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-6">
              <p className="text-gray-500 text-lg max-w-[400px] font-medium leading-relaxed pl-0 lg:pl-8 border-l-0 lg:border-l border-primary/30">
                {t("home.courses.subtitle")}
              </p>
              <Link className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-4 group mt-4 lg:mt-0 shadow-md border border-primary/20" to="/courses">
                <span className="text-[13px] font-bold uppercase tracking-wider">{t("home.courses.allCourses")}</span>
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-primary transition-transform group-hover:translate-x-0.5">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                  </svg>
                </div>
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            /* Skeleton Loaders */
            [1, 2, 3].map(n => (
              <div key={`sk-${n}`} className="flex flex-col animate-pulse rounded-[10px] bg-gray-100 min-h-[480px]">
                <div className="p-10 flex flex-col justify-between h-full">
                  <div>
                    <div className="w-14 h-14 rounded-full bg-gray-200 mb-8" />
                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-6" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-5/6 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-4/6 mb-6" />
                  </div>
                  <div className="mt-auto">
                    <div className="flex gap-4 mb-6">
                      <div className="h-6 bg-gray-200 rounded w-16" />
                      <div className="h-6 bg-gray-200 rounded w-24" />
                    </div>
                    <hr className="border-gray-200 mb-6" />
                    <div className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-28" />
                      <div className="w-8 h-8 rounded-full bg-gray-200" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            coursesData.map((course, index) => {
              const isActive = activeIdx === index;
              return (
                <div 
                  key={course.id || index} 
                  className="flex flex-col"
                >
                <div 
                  onMouseEnter={() => setActiveIdx(index)}
                  onClick={() => setActiveIdx(index)}
                  className={`relative flex flex-col transition-all duration-500 cursor-pointer overflow-hidden rounded-[10px] ${
                    isActive 
                      ? "scale-[1.02] z-10 shadow-xl" 
                      : "hover:-translate-y-1"
                  }`}
                  style={{
                    padding: isActive ? "0px" : "1px",
                    backgroundColor: isActive ? "transparent" : "#D1D5DB", 
                  }}
                >
                  <div 
                    className={`relative overflow-hidden w-full flex-grow min-h-[480px] p-10 flex flex-col justify-between transition-all duration-500 ${
                      isActive 
                        ? "bg-[#0b0f19] shadow-xl" 
                        : "bg-white shadow-[0_5px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.08)]"
                    }`}
                    style={{
                      borderRadius: isActive ? "10px" : "9px"
                    }}
                  >
                    {/* Background Image on Active */}
                    {isActive && (
                      <div className="absolute inset-0 z-0 opacity-30">
                        <img 
                          src={course.img} 
                          alt={course.title} 
                          className="w-full h-full object-cover scale-105 transition-transform duration-700" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f19] via-[#0b0f19]/80 to-transparent" />
                      </div>
                    )}

                    <div className="relative z-10 flex flex-col h-full justify-between flex-grow">
                      <div>
                        {/* Icon container */}
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-8 border transition-all duration-300 ${
                          isActive 
                            ? "bg-primary border-primary text-white" 
                            : "border-[#0b0f19]/15 bg-[#0b0f19]/5 text-primary"
                        }`}>
                          {course.icon}
                        </div>

                        <h1 className={`text-[32px] font-bold leading-[1.15] mb-6 transition-colors duration-300 ${
                          isActive ? "text-white" : "text-[#0b0f19]"
                        }`}>
                          {course.title}
                        </h1>
                      </div>

                      <div className="mt-auto">
                        <p className={`text-[15px] leading-relaxed mb-6 transition-colors duration-300 font-medium ${
                          isActive ? "text-white/80" : "text-gray-500"
                        }`}>
                          {course.desc}
                        </p>

                        <div className="flex items-center gap-4 mb-6">
                          <div className={`font-bold text-[18px] transition-colors duration-300 ${isActive ? "text-white" : "text-[#0b0f19]"}`}>
                            {course.cost}
                          </div>
                          <div className={`w-[1px] h-4 transition-colors duration-300 ${isActive ? "bg-white/20" : "bg-gray-200"}`}></div>
                          <div className={`font-medium text-[14px] flex items-center gap-1.5 transition-colors duration-300 ${isActive ? "text-white/80" : "text-gray-500"}`}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                            {course.duration}
                          </div>
                        </div>

                        <hr className={`mb-6 transition-colors duration-300 ${isActive ? "border-white/10" : "border-gray-100"}`} />
                        <Link to={course.link} className="flex items-center justify-between w-full group/link">
                          <span className={`text-[13px] font-bold uppercase tracking-wider transition-colors duration-300 ${isActive ? "text-white" : "text-[#0b0f19]"}`}>
                            {t("home.courses.viewCurriculum")}
                          </span>
                          <div className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-300 ${
                            isActive 
                              ? "bg-primary border-primary text-white" 
                              : "border-gray-300 text-gray-400 group-hover/link:border-primary group-hover/link:text-primary"
                          }`}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }))}
        </div>
      </div>
    </section>
  );
};

export default ArdrendCourses;
